import React, { useState } from 'react';
import { Modal, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isEmailValid = (email) => EMAIL_REGEX.test(email);

const EmailModal = ({
    open,
    onClose,
    onSend,
    targetEmails,
    setTargetEmails,
    filteredData,
    selectionModel,
}) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const [sendHover, setSendHover] = useState(false);
    const [cancelHover, setCancelHover] = useState(false);

    const emails = Array.isArray(targetEmails) ? targetEmails : [];

    const commitDraft = (value) => {
        const candidate = (value ?? draft).trim().replace(/[,;]+$/, '');
        if (!candidate) return true;
        if (!isEmailValid(candidate)) {
            setError(t('InvalidEmail'));
            return false;
        }
        if (emails.some((e) => e.toLowerCase() === candidate.toLowerCase())) {
            setDraft('');
            setError('');
            return true;
        }
        setTargetEmails([...emails, candidate]);
        setDraft('');
        setError('');
        return true;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ') {
            e.preventDefault();
            commitDraft();
        } else if (e.key === 'Backspace' && draft === '' && emails.length > 0) {
            setTargetEmails(emails.slice(0, -1));
        }
    };

    const handlePaste = (e) => {
        const text = e.clipboardData.getData('text');
        if (!text) return;
        if (/[,;\s]/.test(text)) {
            e.preventDefault();
            const parts = text.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
            const next = [...emails];
            const seen = new Set(next.map((x) => x.toLowerCase()));
            let invalid = '';
            for (const p of parts) {
                if (!isEmailValid(p)) {
                    invalid = p;
                    continue;
                }
                if (!seen.has(p.toLowerCase())) {
                    next.push(p);
                    seen.add(p.toLowerCase());
                }
            }
            setTargetEmails(next);
            setDraft('');
            setError(invalid ? `${t('InvalidEmail')}: ${invalid}` : '');
        }
    };

    const removeEmail = (email) => {
        setTargetEmails(emails.filter((e) => e !== email));
    };

    const handleSend = async () => {
        if (!commitDraft()) return;
        const finalEmails = (() => {
            const candidate = draft.trim();
            if (!candidate) return emails;
            if (!isEmailValid(candidate)) return emails;
            if (emails.some((e) => e.toLowerCase() === candidate.toLowerCase())) return emails;
            return [...emails, candidate];
        })();

        if (finalEmails.length === 0) {
            setError(t('EnterEmailAddress'));
            return;
        }

        onClose();
        await onSend(
            filteredData.filter((r) => selectionModel.includes(r.id)),
            finalEmails,
        );
    };

    const canSend = emails.length > 0 || isEmailValid(draft.trim());

    return (
        <Modal open={open} onClose={onClose}>
            <div
                style={{
                    background: '#fff',
                    borderRadius: '16px',
                    maxWidth: '480px',
                    width: '90%',
                    margin: '10% auto',
                    padding: '24px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    fontFamily: 'Montserrat, sans-serif',
                }}
            >
                <h2
                    style={{
                        margin: 0,
                        fontSize: '20px',
                        color: '#304FFF',
                        textAlign: 'center',
                    }}
                >
                    {t('EnterEmailAddresses')}
                </h2>

                <div
                    style={{
                        border: '1px solid #d0d7e6',
                        borderRadius: '12px',
                        backgroundColor: '#f5f7ff',
                        padding: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        minHeight: '52px',
                    }}
                >
                    {emails.map((email) => (
                        <span
                            key={email}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#304FFF',
                                color: '#fff',
                                padding: '4px 10px',
                                borderRadius: '999px',
                                fontSize: '13px',
                                maxWidth: '100%',
                            }}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {email}
                            </span>
                            <button
                                type="button"
                                onClick={() => removeEmail(email)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    lineHeight: 1,
                                }}
                                aria-label={`remove ${email}`}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <TextField
                        value={draft}
                        onChange={(e) => {
                            setDraft(e.target.value);
                            if (error) setError('');
                        }}
                        onKeyDown={handleKeyDown}
                        onBlur={() => commitDraft()}
                        onPaste={handlePaste}
                        placeholder={emails.length === 0 ? 'name@example.com' : ''}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                            flex: 1,
                            minWidth: '160px',
                            '& input': { padding: '6px 4px', fontSize: '14px' },
                        }}
                    />
                </div>
                <span
                    style={{
                        minHeight: '18px',
                        fontSize: '12px',
                        color: error ? '#d32f2f' : '#666',
                    }}
                >
                    {error || t('EmailChipHint')}
                </span>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <button
                        style={{
                            flex: 1,
                            padding: '12px',
                            borderRadius: '12px',
                            background: sendHover && canSend ? '#203bbd' : '#304FFF',
                            color: '#fff',
                            border: 'none',
                            fontWeight: 'bold',
                            cursor: canSend ? 'pointer' : 'not-allowed',
                            opacity: canSend ? 1 : 0.5,
                            transition: 'background 0.3s ease',
                        }}
                        disabled={!canSend}
                        onMouseEnter={() => setSendHover(true)}
                        onMouseLeave={() => setSendHover(false)}
                        onClick={handleSend}
                    >
                        {t('Send')}
                    </button>

                    <button
                        style={{
                            flex: 1,
                            padding: '12px',
                            borderRadius: '12px',
                            background: cancelHover ? '#e0e0e0' : '#f0f0f0',
                            color: '#333',
                            border: 'none',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'background 0.3s ease',
                        }}
                        onMouseEnter={() => setCancelHover(true)}
                        onMouseLeave={() => setCancelHover(false)}
                        onClick={() => {
                            setTargetEmails([]);
                            setDraft('');
                            setError('');
                            onClose();
                        }}
                    >
                        {t('Cancel')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default EmailModal;
